import { CouponUsage } from "./salesBreakdowns.ts";
import { SALES_DEFINITIONS } from "./salesDefinitions.ts";
import { SalesPeriod } from "./salesPeriod.ts";
import { SaleRecord } from "./SaleRecord.ts";
import { centsForCsv, downloadSalesCsv, toCsv } from "./salesCsv.ts";
import { formatCount, formatEuros, formatPercent } from "./salesFormat.ts";
import { EmptyStatistics, LoadingBlock, Stat } from "./StatisticsCard.tsx";
import { StatisticsCardWithDetails } from "./StatisticsCardWithDetails.tsx";
import { numberOfOrdersOf } from "./salesCustomers.ts";
import {
  CUSTOMER_COUPON_DISCOUNT_COLUMN,
  CUSTOMER_COUPONS_COLUMN,
  CUSTOMER_IDENTITY_COLUMNS,
  CUSTOMER_LAST_ORDER_DATE_COLUMN,
  CUSTOMER_ORDER_COUNT_COLUMN,
  CUSTOMER_SOLD_COLUMN,
  ORDER_AMOUNT_COLUMN,
  ORDER_COUPON_COLUMN,
  ORDER_COUPON_DISCOUNT_COLUMN,
  ORDER_CUSTOMER_COLUMNS,
  ORDER_DATE_COLUMN,
  ORDER_ID_COLUMN,
  orderItemColumn,
} from "./salesDetailsColumns.tsx";

const COUPONS_SHOWN = 8;

export function CouponsCard({
  usage,
  salesWithCoupon,
  itemLabelOf,
  period,
  isLoading,
}: {
  usage: CouponUsage;
  salesWithCoupon: SaleRecord[];
  itemLabelOf: (itemKey: string) => string;
  period: SalesPeriod;
  isLoading: boolean;
}) {
  const exportCsv = () =>
    downloadSalesCsv({
      section: "coupon",
      period,
      csv: toCsv(usage.coupons, [
        { header: "Codice", valueOf: (coupon) => coupon.couponCode },
        { header: "Utilizzi", valueOf: (coupon) => coupon.orderCount },
        {
          header: "Sconto (€)",
          valueOf: (coupon) => centsForCsv(coupon.discountInCents),
        },
        {
          header: "Venduto (€)",
          valueOf: (coupon) => centsForCsv(coupon.soldInCents),
        },
      ]),
    });

  return (
    <StatisticsCardWithDetails
      title="Coupon"
      info={SALES_DEFINITIONS.coupons}
      description="Coupon usati negli ordini conclusi del periodo"
      csvSection="coupon"
      period={period}
      isLoading={isLoading}
      exportSummaryCsv={usage.coupons.length > 0 ? exportCsv : undefined}
      orders={salesWithCoupon}
      orderColumns={[
        ORDER_ID_COLUMN,
        ORDER_DATE_COLUMN,
        ...ORDER_CUSTOMER_COLUMNS,
        ORDER_COUPON_COLUMN,
        orderItemColumn(itemLabelOf),
        ORDER_COUPON_DISCOUNT_COLUMN,
        ORDER_AMOUNT_COLUMN,
      ]}
      customerColumns={[
        ...CUSTOMER_IDENTITY_COLUMNS,
        CUSTOMER_ORDER_COUNT_COLUMN,
        CUSTOMER_COUPONS_COLUMN,
        CUSTOMER_COUPON_DISCOUNT_COLUMN,
        CUSTOMER_SOLD_COLUMN,
        CUSTOMER_LAST_ORDER_DATE_COLUMN,
      ]}
      rankCustomersBy={numberOfOrdersOf}
      noOrdersMessage="Nessun coupon usato nel periodo"
      summary={
        isLoading ? (
          <LoadingBlock className="h-48 w-full" />
        ) : usage.ordersWithCouponCount === 0 ? (
          <EmptyStatistics>Nessun coupon usato nel periodo</EmptyStatistics>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <Stat
                label="Ordini con coupon"
                value={formatCount(usage.ordersWithCouponCount)}
              >
                <span className="text-xs text-gray-500">
                  {formatPercent(usage.shareOfOrders)} degli ordini conclusi
                </span>
              </Stat>
              <Stat
                label="Sconto concesso"
                value={formatEuros(usage.discountInCents)}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium">Codice</th>
                    <th className="py-2 pr-3 font-medium text-right">
                      Utilizzi
                    </th>
                    <th className="py-2 pr-3 font-medium text-right">Sconto</th>
                    <th className="py-2 font-medium text-right">Venduto</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.coupons.slice(0, COUPONS_SHOWN).map((coupon) => (
                    <tr
                      key={coupon.couponCode}
                      className="border-b border-gray-100 text-gray-700"
                    >
                      <td className="py-2 pr-3 font-mono text-xs break-all">
                        {coupon.couponCode}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {formatCount(coupon.orderCount)}
                      </td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">
                        {formatEuros(coupon.discountInCents)}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {formatEuros(coupon.soldInCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      }
    />
  );
}
